import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  IconButton,
  Alert,
  Divider,
  Paper,
  FormHelperText,
  Popover,
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Image as ImageIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Preview as PreviewIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import BrandedWordmark from './BrandedWordmark';
import AdvancedColorPicker from './AdvancedColorPicker';
import { contrastText } from '../utils/colorMath';
import { readBrandingFromStorage, resolveBrandingFromParsed, writeBrandingToStorage } from '../utils/brandingStorage';
import { getActiveTenantProfile } from '../utils/tenantBranding';
import { brandingAPI } from '../services/api';
import { fetchAndCacheTenantBranding } from '../utils/tenantBranding';

const CARD_SX = { border: '1px solid', borderColor: 'divider', boxShadow: 'none', height: '100%' } as const;
const CC_SX = { p: 2, '&:last-child': { pb: 2 } } as const;

const ThemeAndBrandingSettings: React.FC = () => {
  const { currentTheme, setTheme, colors, setCustomColors, refreshTheme, resolvedMode } = useCustomTheme();
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const tenantProfile = getActiveTenantProfile();
  const [companyName, setCompanyName] = useState(tenantProfile.companyName);
  const [companyTagline, setCompanyTagline] = useState(tenantProfile.companyTagline);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [colorPickerAnchor, setColorPickerAnchor] = useState<HTMLButtonElement | null>(null);
  const [draftHex, setDraftHex] = useState(colors.primary);
  
  const logoInputRef = useRef<HTMLInputElement>(null);
  const isDark = resolvedMode === 'dark';

  const themeOptions = [
    { value: 'microsoft', label: 'Microsoft', color: '#0078d4', secondary: '#106ebe', badge: 'Default' },
    { value: 'linkedin',  label: 'LinkedIn',  color: '#0a66c2', secondary: '#0073b1' },
    { value: 'turquoise', label: 'Turquoise', color: '#20B2AA', secondary: '#40E0D0' },
    { value: 'blue',      label: 'Classic Blue', color: '#1976d2', secondary: '#42a5f5' },
    { value: 'purple',    label: 'Purple',    color: '#9c27b0', secondary: '#ba68c8' },
    { value: 'green',     label: 'Green',     color: '#4caf50', secondary: '#81c784' },
    { value: 'orange',    label: 'Orange',    color: '#ff9800', secondary: '#ffb74d' },
  ];

  const handleColorPickerOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDraftHex(colors.primary);
    setColorPickerAnchor(event.currentTarget);
  };

  const handleColorPickerClose = () => {
    setColorPickerAnchor(null);
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        setMessage({ type: 'error', text: 'Logo file size must be less than 2MB' });
        return;
      }
      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: 'Please select an image file' });
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview('');
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    
    try {
      // Save theme color using theme context (which saves to database)
      await setTheme(currentTheme);

      // Persist company branding + logos to backend (tenant_registry)
      await brandingAPI.saveBranding({
        company_name: companyName,
        company_tagline: companyTagline,
        logo: logoPreview || '',
        logo_mark: logoPreview || '',
        logo_on_dark: logoPreview || '',
      });

      // Refresh cached public branding and update local storage so the app bar reflects immediately.
      const profile = await fetchAndCacheTenantBranding();
      writeBrandingToStorage(
        JSON.stringify({
          companyName: profile.companyName,
          companyTagline: profile.companyTagline,
          logo: profile.logo,
          logoMark: profile.logoMark,
          logoOnDark: profile.logoOnDark,
          lastUpdated: new Date().toISOString(),
        })
      );
      
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const loadSavedSettings = () => {
    try {
      const savedBranding = readBrandingFromStorage();
      if (savedBranding) {
        const parsed = JSON.parse(savedBranding) as Record<string, unknown>;
        const resolved = resolveBrandingFromParsed(parsed);
        setCompanyName(resolved.companyName);
        setCompanyTagline(resolved.companyTagline);
        setLogoPreview(resolved.logo);
        return;
      }
      // No saved branding yet → show tenant defaults (includes default logo).
      setCompanyName(tenantProfile.companyName);
      setCompanyTagline(tenantProfile.companyTagline);
      setLogoPreview(tenantProfile.logo);
    } catch (error) {
      console.error('Error loading saved settings:', error);
    }
  };

  React.useEffect(() => {
    loadSavedSettings();
  }, []);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PaletteIcon color="primary" />
        Theme & Branding Settings
      </Typography>

      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Theme Settings */}
        <Grid item xs={12} md={6}>
          <Card sx={CARD_SX}>
            <CardContent sx={CC_SX}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PaletteIcon color="primary" />
                  Theme Color
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={refreshTheme}
                  sx={{ 
                    color: colors.primary,
                    borderColor: colors.primary,
                    '&:hover': {
                      backgroundColor: colors.light,
                      borderColor: colors.primary
                    }
                  }}
                >
                  Refresh
                </Button>
              </Box>
              
              <Alert severity="info" sx={{ mb: 2, fontSize: '0.78rem', py: 0.5 }}>
                Theme applies to <strong>all users</strong> in your organization. Changes are saved to your org settings.
              </Alert>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))',
                  gap: 1,
                  mb: 2,
                }}
              >
                {themeOptions.map((option) => {
                  const selected = option.value === currentTheme;
                  return (
                    <Box
                      key={option.value}
                      component="button"
                      type="button"
                      onClick={() => setTheme(option.value as any)}
                      sx={{
                        textAlign: 'left',
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: selected ? option.color : 'divider',
                        borderRadius: 2,
                        p: 1,
                        bgcolor: selected
                          ? isDark
                            ? `${option.color}22`
                            : `${option.color}10`
                          : 'background.paper',
                        boxShadow: selected ? `0 0 0 2px ${option.color}33` : 'none',
                      }}
                    >
                      <Box
                        sx={{
                          height: 28,
                          borderRadius: 1,
                          mb: 0.75,
                          background: `linear-gradient(90deg, ${option.color}, ${option.secondary})`,
                          position: 'relative',
                        }}
                      >
                        {selected && (
                          <CheckIcon
                            sx={{
                              position: 'absolute',
                              right: 4,
                              top: 4,
                              fontSize: 14,
                              color: contrastText(option.color),
                              bgcolor: 'rgba(255,255,255,0.85)',
                              borderRadius: '50%',
                              p: '1px',
                            }}
                          />
                        )}
                      </Box>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>{option.label}</Typography>
                      {option.badge && (
                        <Typography sx={{ fontSize: '0.62rem', color: option.color, fontWeight: 700 }}>
                          {option.badge}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>

              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<PaletteIcon />}
                  onClick={handleColorPickerOpen}
                  sx={{
                    borderColor: colors.primary,
                    color: colors.primary,
                    textTransform: 'none',
                    fontWeight: 700,
                    '&:hover': {
                      backgroundColor: `${colors.primary}10`,
                      borderColor: colors.primary,
                    }
                  }}
                >
                  Advanced color picker
                </Button>
                
                <Popover
                  open={Boolean(colorPickerAnchor)}
                  anchorEl={colorPickerAnchor}
                  onClose={handleColorPickerClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  PaperProps={{
                    sx: {
                      p: 2,
                      width: 340,
                      borderRadius: 2.5,
                      border: '1px solid',
                      borderColor: 'divider',
                    },
                  }}
                >
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, mb: 0.5 }}>
                    Custom brand color
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 1.5 }}>
                    2D canvas + hue, hex, and RGB controls
                  </Typography>
                  <AdvancedColorPicker
                    value={draftHex}
                    onChange={setDraftHex}
                    compact
                    showApply
                    onApply={async (palette) => {
                      await setCustomColors(palette);
                      handleColorPickerClose();
                    }}
                  />
                </Popover>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Company Branding */}
        <Grid item xs={12} md={6}>
          <Card sx={CARD_SX}>
            <CardContent sx={CC_SX}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ImageIcon color="primary" />
                Company Branding
              </Typography>
              
              <TextField
                fullWidth
                label="Company Name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                sx={{ mb: 2 }}
              />
              
              <TextField
                fullWidth
                label="Company Tagline"
                value={companyTagline}
                onChange={(e) => setCompanyTagline(e.target.value)}
                sx={{ mb: 2 }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Logo Upload */}
        <Grid item xs={12} md={6}>
          <Card sx={CARD_SX}>
            <CardContent sx={CC_SX}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <UploadIcon color="primary" />
                Logo Upload
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  style={{ display: 'none' }}
                />
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => logoInputRef.current?.click()}
                  sx={{ mb: 1.5 }}
                >
                  Upload Logo
                </Button>
                <FormHelperText>
                  Recommended: 200x60px, PNG/SVG format, max 2MB
                </FormHelperText>
              </Box>

              {logoPreview && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600}>
                      Logo preview
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Used across the app header and login screens
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>
                    <Box
                      component="img"
                      src={logoPreview}
                      alt="Logo preview"
                      sx={{
                        maxWidth: 200,
                        height: 44,
                        objectFit: 'contain',
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        px: 1,
                      }}
                    />
                    <IconButton color="error" onClick={handleRemoveLogo} size="small" aria-label="Remove logo">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Preview Section */}
        <Grid item xs={12}>
          <Card sx={CARD_SX}>
            <CardContent sx={CC_SX}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PreviewIcon color="primary" />
                Brand Preview
              </Typography>
              
              <Paper variant="outlined" sx={{ p: 2.5, bgcolor: 'background.paper', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  {logoPreview && (
                    <Box
                      component="img"
                      src={logoPreview}
                      alt="Brand logo"
                      sx={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 1 }}
                    />
                  )}
                  <Box>
                    <BrandedWordmark name={companyName} variant="h6" sx={{ color: 'primary.main' }} />
                    <Typography variant="body2" color="text.secondary">
                      {companyTagline}
                    </Typography>
                  </Box>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="body2" color="text.secondary">
                  This is how your branding will appear throughout the application.
                  The theme color will be applied to buttons, links, and highlights.
                </Typography>
              </Paper>
            </CardContent>
          </Card>
        </Grid>

        {/* Save Button */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => window.location.reload()}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ThemeAndBrandingSettings;
